import { CheckIcon, ClipboardDocumentIcon, LinkIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useCopyToClipboard } from 'usehooks-ts';
import { Button } from '../../../components/Button';
import { T, useT } from '../../../translation/src';

// The link is built where it is shown: the token is the server's, the origin is wherever the app runs.
export const buildReferralUrl = (token: string) => `${window.location.origin}/register?ref=${encodeURIComponent(token)}`;

const COPIED_FEEDBACK_MS = 2000;

interface ReferralLinkCardProps {
  token?: string | null;
  onGenerate: () => void;
  generating?: boolean;
  // To a partner the link is how a customer registers with it: worded as such.
  partner?: boolean;
}
const ReferralLinkCard_: React.FC<ReferralLinkCardProps> = ({ token, onGenerate, generating, partner }) => {
  const copyFailed = useT('could not copy the link', { swc: true });
  const [, copy] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  const link = useMemo(() => (token ? buildReferralUrl(token) : ''), [token]);
  const onCopy = useCallback(() => {
    void copy(link).then((ok) => (ok ? setCopied(true) : toast.error(copyFailed)));
  }, [copy, copyFailed, link]);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-[#242424]">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-dark-green-700 dark:border-white/10 dark:bg-white/5 dark:text-neon-green-400">
          <LinkIcon className="h-5 w-5" />
        </span>
        <div className="flex flex-col">
          <span className="text-base font-semibold">{partner ? <T _str="register or invite a customer" swc /> : <T _str="your referral link" swc />}</span>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {partner ? (
              <T _str="Customers who register through this link are linked to you as their partner, and get an extra free project." />
            ) : (
              <T _str="They get a free project when they sign up. You get one for every subscription they pay for." />
            )}
          </span>
        </div>
      </div>
      {token ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            readOnly
            value={link}
            className="w-full grow rounded-xl border border-dashed border-neon-green-500/60 bg-gray-50 px-4 py-3 font-mono text-xs text-gray-800 dark:bg-eerie-black dark:text-gray-100"
            onFocus={(event) => event.target.select()}
          />
          <Button variant="primary" size="md" onClick={onCopy} className="shrink-0 justify-center">
            {copied ? <CheckIcon className="mr-1 h-4 w-4" /> : <ClipboardDocumentIcon className="mr-1 h-4 w-4" />}
            {copied ? <T _str="copied" swc /> : <T _str="copy link" swc />}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            <T _str="You have no link yet. Make one and share it. It never expires." />
          </span>
          <Button variant="primary" size="md" onClick={onGenerate} loading={generating} className="shrink-0">
            {partner ? <T _str="generate registration link" swc /> : <T _str="generate referral link" swc />}
          </Button>
        </div>
      )}
    </div>
  );
};
export const ReferralLinkCard = memo(ReferralLinkCard_);
